import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatMoney, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import Logo from "@/components/Logo";
import AdminCotizador from "@/pages/AdminCotizador";
import AdminVentas from "@/pages/AdminVentas";
import AdminReportes from "@/pages/AdminReportes";
import AdminContratos from "@/pages/AdminContratos";
import AdminFinanciero from "@/pages/AdminFinanciero";

const CATEGORIES = [
    { key: "wedding_planner", label: "Wedding Planner" },
    { key: "catering", label: "Catering" },
    { key: "decoracion", label: "Decoración" },
    { key: "musica", label: "Música" },
    { key: "fotografia", label: "Fotografía" },
    { key: "video", label: "Video" },
    { key: "estructuras", label: "Estructuras" },
];

const TIER_LABELS = { basico: "Básico", normal: "Estándar", premium: "Premium" };

export default function AdminDashboard() {
    const { user, logout } = useAuth();

    return (
        <div className="min-h-screen bg-[#FAFAFA]">
            <header className="bg-white border-b border-border">
                <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Logo variant="seal" size={44} />
                        <div className="flex items-baseline gap-4">
                            <span className="font-display text-2xl">Hacienda Nápoles</span>
                            <span className="overline text-muted-foreground">Panel administrativo</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="overline text-muted-foreground hidden md:inline">{user?.email}</span>
                        <Button variant="ghost" size="sm" onClick={logout} data-testid="btn-logout" className="rounded-none">
                            <LogOut className="h-4 w-4 mr-2" /> Salir
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto p-6">
                <Tabs defaultValue="leads" className="w-full">
                    <TabsList className="rounded-none bg-transparent border-b border-border justify-start overflow-x-auto w-full">
                        <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-none">Leads</TabsTrigger>
                        <TabsTrigger value="cotizador" data-testid="tab-cotizador" className="rounded-none">Cotizador</TabsTrigger>
                        <TabsTrigger value="ventas" data-testid="tab-ventas" className="rounded-none">Ventas</TabsTrigger>
                        <TabsTrigger value="financiero" data-testid="tab-financiero" className="rounded-none">Financiero</TabsTrigger>
                        <TabsTrigger value="contratos" data-testid="tab-contratos" className="rounded-none">Contratos</TabsTrigger>
                        <TabsTrigger value="reportes" data-testid="tab-reportes" className="rounded-none">Reportes</TabsTrigger>
                        <TabsTrigger value="calendar" data-testid="tab-calendar" className="rounded-none">Calendario</TabsTrigger>
                        <TabsTrigger value="pricing" data-testid="tab-pricing" className="rounded-none">Tarificadores</TabsTrigger>
                        <TabsTrigger value="providers" data-testid="tab-providers" className="rounded-none">Proveedores</TabsTrigger>
                        <TabsTrigger value="extras" data-testid="tab-extras" className="rounded-none">Serv. Adicionales</TabsTrigger>
                        <TabsTrigger value="faq" data-testid="tab-faq" className="rounded-none">FAQ</TabsTrigger>
                        <TabsTrigger value="benefits" data-testid="tab-benefits" className="rounded-none">Beneficios</TabsTrigger>
                        <TabsTrigger value="anniv" data-testid="tab-anniv" className="rounded-none">Aniversarios</TabsTrigger>
                        <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-none">Clientes</TabsTrigger>
                        <TabsTrigger value="notify" data-testid="tab-notify" className="rounded-none">Notificaciones</TabsTrigger>
                    </TabsList>

                    <TabsContent value="leads" className="mt-6"><LeadsTab /></TabsContent>
                    <TabsContent value="cotizador" className="mt-6"><AdminCotizador /></TabsContent>
                    <TabsContent value="ventas" className="mt-6"><AdminVentas /></TabsContent>
                    <TabsContent value="financiero" className="mt-6"><AdminFinanciero /></TabsContent>
                    <TabsContent value="contratos" className="mt-6"><AdminContratos /></TabsContent>
                    <TabsContent value="reportes" className="mt-6"><AdminReportes /></TabsContent>
                    <TabsContent value="calendar" className="mt-6"><CalendarTab /></TabsContent>
                    <TabsContent value="pricing" className="mt-6"><PricingTab /></TabsContent>
                    <TabsContent value="providers" className="mt-6"><ProvidersTab /></TabsContent>
                    <TabsContent value="extras" className="mt-6"><ExtrasTab /></TabsContent>
                    <TabsContent value="faq" className="mt-6"><FaqTab /></TabsContent>
                    <TabsContent value="benefits" className="mt-6"><BenefitsTab /></TabsContent>
                    <TabsContent value="anniv" className="mt-6"><AnniversaryTab /></TabsContent>
                    <TabsContent value="customers" className="mt-6"><CustomersTab /></TabsContent>
                    <TabsContent value="notify" className="mt-6"><NotifyTab /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

// ---------------- LEADS (con filtros) ----------------
function LeadsTab() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/leads"); setLeads(data); }
        catch (err) { /* noop */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        return leads.filter((l) => {
            if (search) {
                const s = search.toLowerCase();
                const hay = `${l.contact_name || ""} ${l.email || ""} ${l.phone || ""} ${l.bride_name || ""} ${l.groom_name || ""}`.toLowerCase();
                if (!hay.includes(s)) return false;
            }
            if (status !== "all") {
                const st = l.is_mega_event ? "mega" : (l.status || "completo");
                if (st !== status) return false;
            }
            const created = (l.created_at || "").slice(0, 10);
            if (from && created < from) return false;
            if (to && created > to) return false;
            return true;
        });
    }, [leads, search, status, from, to]);

    const statusBadge = (l) => {
        if (l.is_mega_event) return <span className="bg-accent text-accent-foreground px-2 py-0.5 text-[10px] overline">Mega</span>;
        if (l.status === "borrador") return <span className="bg-muted text-muted-foreground px-2 py-0.5 text-[10px] overline">Borrador</span>;
        return <span className="bg-primary text-primary-foreground px-2 py-0.5 text-[10px] overline">Completo</span>;
    };

    return (
        <div className="bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className="font-display text-2xl">Leads recibidos</h3>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={load} className="rounded-none" data-testid="btn-refresh-leads">
                        <RefreshCw className="h-3 w-3 mr-1" /> Recargar
                    </Button>
                    <a href={`${BACKEND_URL}/api/admin/leads/export.csv`} target="_blank" rel="noreferrer" data-testid="btn-export-csv">
                        <Button size="sm" className="rounded-none bg-foreground text-background hover:bg-foreground/85">
                            <Download className="h-3 w-3 mr-1" /> CSV
                        </Button>
                    </a>
                </div>
            </div>

            {/* Filtros */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="leads-filters">
                <Input placeholder="Buscar cliente / email / teléfono" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none text-xs" data-testid="filter-search" />
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-border rounded-none text-xs px-2 bg-white" data-testid="filter-status">
                    <option value="all">Todos los estados</option>
                    <option value="borrador">Borrador</option>
                    <option value="completo">Completo</option>
                    <option value="mega">Mega evento</option>
                </select>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none text-xs" data-testid="filter-from" />
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none text-xs" data-testid="filter-to" />
            </div>

            {loading ? <div className="overline text-muted-foreground">Cargando…</div> : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="leads-table">
                        <thead className="bg-secondary">
                            <tr>
                                {["Fecha", "Contacto", "Evento", "Invitados", "Estado", "Canal", "Total"].map((h) => (
                                    <th key={h} className="text-left px-3 py-2 overline">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((l) => (
                                <tr key={l.id} className="border-t border-border hover:bg-secondary/50">
                                    <td className="px-3 py-2 tabular">{(l.created_at || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2">{l.contact_name}<br/><span className="text-muted-foreground">{l.email || l.phone}</span></td>
                                    <td className="px-3 py-2">
                                        <span className="font-display text-sm">{l.event_type || "Boda"}</span>
                                        {l.bride_name && l.groom_name && <><br/><span className="text-muted-foreground">{l.bride_name} & {l.groom_name}</span></>}
                                    </td>
                                    <td className="px-3 py-2 tabular">{l.guests ?? "—"}</td>
                                    <td className="px-3 py-2">{statusBadge(l)}</td>
                                    <td className="px-3 py-2">{l.channel}</td>
                                    <td className="px-3 py-2 tabular text-right">{l.total != null ? formatMoney(l.total) : "—"}</td>
                                </tr>
                            ))}
                            {filtered.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-10 overline">Sin leads con estos filtros</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ---------------- CALENDARIO ----------------
const BLOCK_COLORS = {
    reservado: "bg-accent text-accent-foreground",
    tentativo: "bg-secondary text-foreground",
    mantenimiento: "bg-muted-foreground/70 text-white",
    bloqueado: "bg-destructive/80 text-white",
};
const MONTHS_LONG = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function CalendarTab() {
    const today = new Date();
    const [ym, setYm] = useState({ y: today.getFullYear(), m: today.getMonth() });
    const [blocks, setBlocks] = useState([]);
    const [events, setEvents] = useState([]);
    const [view, setView] = useState("month");
    const [selected, setSelected] = useState(null); // {date, block?}
    const [draft, setDraft] = useState({ status: "reservado", note: "" });

    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/calendar"); setBlocks(data); }
        catch (err) { /* noop */ }
        try { const { data: ev } = await api.get("/admin/calendar-events"); setEvents(ev); }
        catch (err) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const byDate = useMemo(() => Object.fromEntries(blocks.map((b) => [b.date, b])), [blocks]);
    const eventsByDate = useMemo(() => {
        const map = {};
        for (const e of events) { (map[e.date] = map[e.date] || []).push(e); }
        return map;
    }, [events]);

    const days = useMemo(() => {
        const first = new Date(ym.y, ym.m, 1);
        const startDow = (first.getDay() + 6) % 7; // lunes = 0
        const total = new Date(ym.y, ym.m + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < startDow; i++) cells.push(null);
        for (let d = 1; d <= total; d++) cells.push(d);
        return cells;
    }, [ym]);

    const dateStr = (d) => `${ym.y}-${String(ym.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

    const openDay = (d) => {
        const ds = dateStr(d);
        const block = byDate[ds];
        setSelected({ date: ds, block });
        setDraft({ status: block?.status || "reservado", note: block?.note || "" });
    };

    const save = async () => {
        try {
            await api.post("/admin/calendar", { date: selected.date, status: draft.status, note: draft.note });
            toast.success(`Fecha ${selected.date} bloqueada (${draft.status})`);
            setSelected(null);
            load();
        } catch (_e) { toast.error("Error al guardar bloqueo"); }
    };

    const removeBlock = async (block) => {
        try {
            await api.delete(`/admin/calendar/${block.id}`);
            toast.success("Fecha habilitada nuevamente");
            setSelected(null);
            load();
        } catch (_e) { toast.error("Error al eliminar"); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-border p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h3 className="font-display text-2xl">Calendario de eventos</h3>
                    <div className="flex gap-2 items-center">
                        <button onClick={() => setView("month")} className={`px-3 py-1.5 text-xs overline border ${view === "month" ? "bg-foreground text-background border-foreground" : "border-border"}`} data-testid="view-month">Mes</button>
                        <button onClick={() => setView("list")} className={`px-3 py-1.5 text-xs overline border ${view === "list" ? "bg-foreground text-background border-foreground" : "border-border"}`} data-testid="view-list">Lista</button>
                    </div>
                </div>

                {view === "month" ? (
                    <>
                        <div className="flex items-center justify-between mb-3">
                            <Button variant="ghost" size="sm" onClick={() => setYm(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 })} className="rounded-none" data-testid="cal-prev">
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <div className="font-display text-xl" data-testid="cal-month-label">{MONTHS_LONG[ym.m]} {ym.y}</div>
                            <Button variant="ghost" size="sm" onClick={() => setYm(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 })} className="rounded-none" data-testid="cal-next">
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center overline text-[10px] text-muted-foreground mb-1">
                            {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => <div key={d}>{d}</div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                            {days.map((d, i) => {
                                if (!d) return <div key={`empty-${i}`} />;
                                const ds = dateStr(d);
                                const block = byDate[ds];
                                const evs = eventsByDate[ds] || [];
                                return (
                                    <button
                                        key={d}
                                        type="button"
                                        onClick={() => openDay(d)}
                                        data-testid={`cal-day-${d}`}
                                        className={`aspect-square border text-xs flex flex-col items-center justify-center gap-0.5 transition hover:border-foreground ${block ? BLOCK_COLORS[block.status] + " border-transparent" : "border-border bg-white"}`}
                                        title={evs.map((e) => `${e.kind === "efectiva" ? "✓ Venta" : "◌ Tentativa"}: ${e.name}`).join("\n") || undefined}
                                    >
                                        <span className="tabular">{d}</span>
                                        {block && <span className="text-[8px] uppercase tracking-wider">{block.status.slice(0, 4)}</span>}
                                        {evs.length > 0 && (
                                            <span className="flex gap-0.5">
                                                {evs.slice(0, 3).map((e, j) => (
                                                    <span key={j} className={`inline-block h-1.5 w-1.5 rounded-full ${e.kind === "efectiva" ? "bg-eucalyptus" : "bg-accent"}`} />
                                                ))}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex flex-wrap gap-4 mt-4 text-[10px] overline text-muted-foreground">
                            <span><span className="inline-block h-3 w-3 bg-accent mr-1 align-middle" /> Reservado</span>
                            <span><span className="inline-block h-3 w-3 bg-secondary border border-border mr-1 align-middle" /> Tentativo</span>
                            <span><span className="inline-block h-3 w-3 bg-destructive/80 mr-1 align-middle" /> Bloqueado</span>
                            <span><span className="inline-block h-3 w-3 bg-muted-foreground/70 mr-1 align-middle" /> Mantenimiento</span>
                            <span><span className="inline-block h-2 w-2 rounded-full bg-eucalyptus mr-1 align-middle" /> Reserva efectiva (venta)</span>
                            <span><span className="inline-block h-2 w-2 rounded-full bg-accent mr-1 align-middle" /> Reserva tentativa (cotización)</span>
                        </div>
                    </>
                ) : (
                    <div className="space-y-2" data-testid="calendar-list">
                        {blocks.length === 0 && <div className="overline text-muted-foreground">Sin fechas bloqueadas</div>}
                        {blocks.map((b) => (
                            <div key={b.id} className="flex items-center justify-between border border-border p-3 text-sm">
                                <div>
                                    <span className="tabular font-medium">{b.date}</span>
                                    <span className={`ml-3 px-2 py-0.5 text-[10px] overline ${BLOCK_COLORS[b.status]}`}>{b.status}</span>
                                    {b.note && <span className="ml-3 text-muted-foreground text-xs">{b.note}</span>}
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => removeBlock(b)} className="rounded-none text-destructive" data-testid={`del-block-${b.date}`}>
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-3">Bloqueo de fecha</h3>
                {!selected ? (
                    <div className="overline text-muted-foreground">Selecciona un día del calendario para bloquearlo o editarlo.</div>
                ) : (
                    <div className="space-y-4" data-testid="block-editor">
                        <div className="font-display text-3xl tabular">{selected.date}</div>
                        <Field label="Estado">
                            <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="w-full border border-border rounded-none px-2 py-2 bg-white text-sm" data-testid="block-status">
                                <option value="reservado">Reservado</option>
                                <option value="tentativo">Tentativo</option>
                                <option value="bloqueado">Bloqueado</option>
                                <option value="mantenimiento">Mantenimiento</option>
                            </select>
                        </Field>
                        <Field label="Motivo (mantenimiento / uso personal / cliente)">
                            <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} className="rounded-none" data-testid="block-note" />
                        </Field>
                        <div className="flex gap-2">
                            <Button onClick={save} className="rounded-none bg-primary text-primary-foreground" data-testid="btn-save-block">Guardar bloqueo</Button>
                            {selected.block && (
                                <Button variant="ghost" onClick={() => removeBlock(selected.block)} className="rounded-none text-destructive" data-testid="btn-unblock">Habilitar fecha</Button>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">Las fechas <strong>Reservadas</strong> y <strong>Tentativas</strong> se deshabilitan en el cotizador público. Las fechas <strong>Bloqueadas</strong> o en <strong>Mantenimiento</strong> permiten cotizar (con aviso), pero el sistema rechaza registrar una reserva/venta en ellas.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------- TARIFICADORES ----------------
function PricingTab() {
    const [pricing, setPricing] = useState(null);
    const [loc, setLoc] = useState(null);

    useEffect(() => { (async () => {
        const a = await api.get("/admin/pricing"); setPricing(a.data);
        const b = await api.get("/admin/location"); setLoc(b.data);
    })(); }, []);

    const savePricing = async () => {
        try {
            await api.put("/admin/pricing", {
                base_price: Number(pricing.base_price),
                base_guests_included: Number(pricing.base_guests_included),
                extra_guest_tiers: (pricing.extra_guest_tiers || []).map((t) => ({ up_to: Number(t.up_to), price_per_extra: Number(t.price_per_extra) })),
                carpa_20: numObj(pricing.carpa_20),
                carpa_25: numObj(pricing.carpa_25),
                staff_tiers: (pricing.staff_tiers || []).map((t) => ({ up_to: Number(t.up_to), staff: Number(t.staff) })),
                high_months: pricing.high_months || [],
                high_season_surcharge_pct: Number(pricing.high_season_surcharge_pct) || 0,
            });
            toast.success("Tarificador actualizado");
        } catch (_e) { toast.error("Error al guardar"); }
    };
    const saveLoc = async () => {
        try {
            await api.put("/admin/location", {
                capilla_price: Number(loc.capilla_price || 0),
                jardines_price: Number(loc.jardines_price || 0),
                iglesia_externa_price: Number(loc.iglesia_externa_price || 0),
                isla_exclusivity_fee: Number(loc.isla_exclusivity_fee),
                isla_logistics_surcharge_pct: Number(loc.isla_logistics_surcharge_pct),
            });
            toast.success("Isla actualizada");
        } catch (_e) { toast.error("Error al guardar"); }
    };

    if (!pricing || !loc) return <div className="overline text-muted-foreground">Cargando…</div>;

    const setTier = (i, key, val) => {
        const tiers = [...(pricing.extra_guest_tiers || [])];
        tiers[i] = { ...tiers[i], [key]: val };
        setPricing({ ...pricing, extra_guest_tiers: tiers });
    };
    const setStaffTier = (i, key, val) => {
        const tiers = [...(pricing.staff_tiers || [])];
        tiers[i] = { ...tiers[i], [key]: val };
        setPricing({ ...pricing, staff_tiers: tiers });
    };
    const setCarpa = (which, key, val) => {
        setPricing({ ...pricing, [which]: { ...(pricing[which] || {}), [key]: val } });
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-border p-6 space-y-5">
                <h3 className="font-display text-2xl">Tarifa base y aforo</h3>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Tarifa base (USD)">
                        <Input type="number" value={pricing.base_price} onChange={(e) => setPricing({ ...pricing, base_price: e.target.value })} className="rounded-none tabular" data-testid="input-base-price" />
                    </Field>
                    <Field label="Invitados incluidos en base">
                        <Input type="number" value={pricing.base_guests_included} onChange={(e) => setPricing({ ...pricing, base_guests_included: e.target.value })} className="rounded-none tabular" data-testid="input-base-guests" />
                    </Field>
                </div>

                <div>
                    <div className="overline text-muted-foreground mb-2">Matriz escalonada — invitados extra (201+)</div>
                    {(pricing.extra_guest_tiers || []).map((t, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2 items-center">
                            <Input type="number" value={t.up_to} onChange={(e) => setTier(i, "up_to", e.target.value)} className="rounded-none tabular text-xs" data-testid={`tier-upto-${i}`} />
                            <Input type="number" step="0.5" value={t.price_per_extra} onChange={(e) => setTier(i, "price_per_extra", e.target.value)} className="rounded-none tabular text-xs" data-testid={`tier-price-${i}`} />
                            <Button variant="ghost" size="sm" onClick={() => setPricing({ ...pricing, extra_guest_tiers: pricing.extra_guest_tiers.filter((_, j) => j !== i) })} className="rounded-none text-destructive"><Trash2 className="h-3 w-3" /></Button>
                        </div>
                    ))}
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-[10px] overline text-muted-foreground">
                        <span>Hasta N invitados</span><span>$ por invitado extra</span><span />
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setPricing({ ...pricing, extra_guest_tiers: [...(pricing.extra_guest_tiers || []), { up_to: 750, price_per_extra: 12 }] })} className="rounded-none mt-2" data-testid="btn-add-tier">+ Rango</Button>
                </div>

                <div>
                    <div className="overline text-muted-foreground mb-2">Personal operativo incluido</div>
                    {(pricing.staff_tiers || []).map((t, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                            <Input type="number" value={t.up_to} onChange={(e) => setStaffTier(i, "up_to", e.target.value)} className="rounded-none tabular text-xs" />
                            <Input type="number" value={t.staff} onChange={(e) => setStaffTier(i, "staff", e.target.value)} className="rounded-none tabular text-xs" />
                        </div>
                    ))}
                    <div className="grid grid-cols-2 gap-2 text-[10px] overline text-muted-foreground"><span>Hasta N invitados</span><span># personas incluidas</span></div>
                </div>

                <div>
                    <div className="overline text-muted-foreground mb-2">Temporada alta — meses y recargo</div>
                    <div className="grid grid-cols-6 gap-1 mb-3">
                        {["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"].map((m, i) => {
                            const n = i + 1;
                            const isHigh = (pricing.high_months || []).includes(n);
                            return (
                                <button key={n} type="button" data-testid={`month-cfg-${n}`}
                                    onClick={() => {
                                        const hm = isHigh ? pricing.high_months.filter((x) => x !== n) : [...(pricing.high_months || []), n].sort((a, b) => a - b);
                                        setPricing({ ...pricing, high_months: hm });
                                    }}
                                    className={`border p-2 text-[10px] ${isHigh ? "bg-accent text-white border-accent" : "bg-white border-border"}`}>
                                    <div className="font-display text-xs">{m}</div>
                                    <div className="overline text-[8px] mt-0.5">{isHigh ? "Alta" : "Baja"}</div>
                                </button>
                            );
                        })}
                    </div>
                    <Field label="Recargo temporada alta (% sobre tarifa base)">
                        <Input type="number" step="0.5" value={pricing.high_season_surcharge_pct ?? 0} onChange={(e) => setPricing({ ...pricing, high_season_surcharge_pct: e.target.value })} className="rounded-none tabular" data-testid="input-season-pct" />
                    </Field>
                </div>

                <Button onClick={savePricing} className="rounded-none bg-foreground text-background" data-testid="btn-save-pricing">Guardar tarificador</Button>
            </div>

            <div className="space-y-4">
                {["carpa_20", "carpa_25"].map((which) => {
                    const c = pricing[which] || {};
                    return (
                        <div key={which} className="bg-white border border-border p-6">
                            <h3 className="font-display text-2xl mb-3">Carpa modular {which === "carpa_20" ? "20m" : "25m"}</h3>
                            <div className="grid grid-cols-3 gap-3">
                                <Field label="$ / m²"><Input type="number" step="0.1" value={c.price_per_m2} onChange={(e) => setCarpa(which, "price_per_m2", e.target.value)} className="rounded-none tabular text-xs" data-testid={`${which}-m2`} /></Field>
                                <Field label="$ / módulo"><Input type="number" value={c.module_price} onChange={(e) => setCarpa(which, "module_price", e.target.value)} className="rounded-none tabular text-xs" data-testid={`${which}-modprice`} /></Field>
                                <Field label="m² / módulo"><Input type="number" value={c.module_area_m2} onChange={(e) => setCarpa(which, "module_area_m2", e.target.value)} className="rounded-none tabular text-xs" /></Field>
                                <Field label="Pax / módulo"><Input type="number" step="0.5" value={c.guests_per_module} onChange={(e) => setCarpa(which, "guests_per_module", e.target.value)} className="rounded-none tabular text-xs" /></Field>
                                <Field label="Máx. módulos"><Input type="number" value={c.max_modules} onChange={(e) => setCarpa(which, "max_modules", e.target.value)} className="rounded-none tabular text-xs" /></Field>
                                <Field label="Flete (USD)"><Input type="number" value={c.flete} onChange={(e) => setCarpa(which, "flete", e.target.value)} className="rounded-none tabular text-xs" data-testid={`${which}-flete`} /></Field>
                            </div>
                        </div>
                    );
                })}

                <div className="bg-white border border-border p-6">
                    <h3 className="font-display text-2xl mb-3">Upgrade Premium — La Isla</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Fee de exclusividad (USD)">
                            <Input type="number" value={loc.isla_exclusivity_fee} onChange={(e) => setLoc({ ...loc, isla_exclusivity_fee: e.target.value })} className="rounded-none tabular" data-testid="input-isla-fee" />
                        </Field>
                        <Field label="Recargo logístico (%)">
                            <Input type="number" value={loc.isla_logistics_surcharge_pct} onChange={(e) => setLoc({ ...loc, isla_logistics_surcharge_pct: e.target.value })} className="rounded-none tabular" data-testid="input-isla-pct" />
                        </Field>
                    </div>
                    <Button onClick={saveLoc} className="mt-4 rounded-none bg-foreground text-background" data-testid="btn-save-isla">Guardar Isla</Button>
                </div>
            </div>
        </div>
    );
}

function numObj(o) {
    const out = {};
    for (const [k, v] of Object.entries(o || {})) out[k] = Number(v);
    return out;
}

// ---------------- PROVEEDORES ----------------
function ProvidersTab() {
    const [providers, setProviders] = useState([]);
    const [editing, setEditing] = useState(null);
    const [filterCategory, setFilterCategory] = useState("all");

    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/providers"); setProviders(data); }
        catch (err) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const startNew = () => setEditing({
        _isNew: true, category: "wedding_planner", tier: "basico", brand: "", name: "",
        description: "", price: 0, price_mode: "flat", instagram_url: "", logo_url: "",
        all_inclusive: false, active: true, included: [],
    });

    const save = async () => {
        try {
            const payload = {
                category: editing.category,
                tier: editing.tier,
                brand: editing.brand || null,
                name: editing.name,
                description: editing.description,
                price: Number(editing.price) || 0,
                price_mode: editing.price_mode,
                instagram_url: editing.instagram_url || null,
                logo_url: editing.logo_url || null,
                all_inclusive: !!editing.all_inclusive,
                active: editing.active !== false,
                included: editing.included || [],
            };
            if (editing._isNew) {
                await api.post("/admin/providers", payload);
                toast.success("Proveedor creado");
            } else {
                await api.put(`/admin/providers/${editing.id}`, payload);
                toast.success("Proveedor actualizado");
            }
            setEditing(null);
            load();
        } catch (_e) { toast.error("Error al guardar"); }
    };

    const remove = async () => {
        if (!editing?.id || editing._isNew) return;
        if (!window.confirm(`¿Eliminar "${editing.name}"?`)) return;
        try {
            await api.delete(`/admin/providers/${editing.id}`);
            toast.success("Proveedor eliminado");
            setEditing(null);
            load();
        } catch (_e) { toast.error("Error al eliminar"); }
    };

    const grouped = useMemo(() => {
        const filtered = filterCategory === "all" ? providers : providers.filter((p) => p.category === filterCategory);
        const map = new Map();
        for (const p of filtered) {
            const bKey = `${p.category}::${p.brand || p.name}`;
            if (!map.has(bKey)) map.set(bKey, { category: p.category, brand: p.brand || p.name, pkgs: [] });
            map.get(bKey).pkgs.push(p);
        }
        return Array.from(map.values()).sort((a, b) => (a.category + a.brand).localeCompare(b.category + b.brand));
    }, [providers, filterCategory]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-border p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <h3 className="font-display text-2xl">Proveedores por marca</h3>
                    <div className="flex gap-2">
                        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="border border-border rounded-none text-xs px-2 py-1.5 bg-white" data-testid="filter-category">
                            <option value="all">Todas las categorías</option>
                            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                        <Button size="sm" onClick={startNew} className="rounded-none bg-primary text-primary-foreground" data-testid="btn-new-provider">
                            + Nuevo proveedor
                        </Button>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                    Cada <strong>marca</strong> puede tener hasta 3 paquetes (Básico, Estándar, Premium). El flag <strong>Todo Incluido</strong> aplica a Wedding Planners: al activarlo, la app bloquea Catering, Decoración, Música, Fotografía, Video y Estructura al cliente.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="providers-table">
                        <thead className="bg-secondary">
                            <tr>{["Categoría", "Marca / Paquete", "Nivel", "Precio", "Estado", ""].map((h) => (
                                <th key={h} className="text-left px-3 py-2 overline">{h}</th>
                            ))}</tr>
                        </thead>
                        <tbody>
                            {grouped.map((g) => (
                                <React.Fragment key={`${g.category}-${g.brand}`}>
                                    <tr className="bg-muted/60">
                                        <td className="px-3 py-2 text-[10px] overline text-muted-foreground">{g.category}</td>
                                        <td className="px-3 py-2 font-serif italic text-base" colSpan={4}>
                                            {g.brand}
                                            <span className="ml-2 overline text-muted-foreground text-[9px]">{g.pkgs.length} paquete(s)</span>
                                        </td>
                                        <td></td>
                                    </tr>
                                    {g.pkgs.sort((a, b) => ({ basico: 0, normal: 1, premium: 2 }[a.tier] - { basico: 0, normal: 1, premium: 2 }[b.tier])).map((p) => (
                                        <tr key={p.id} className="border-t border-border">
                                            <td className="px-3 py-2"></td>
                                            <td className="px-3 py-2 pl-6">{p.name}</td>
                                            <td className="px-3 py-2">{TIER_LABELS[p.tier] || p.tier}</td>
                                            <td className="px-3 py-2 tabular">{formatMoney(p.price)}{p.price_mode === "per_guest" ? " /pax" : ""}</td>
                                            <td className="px-3 py-2">
                                                {p.all_inclusive && <span className="bg-accent text-accent-foreground px-2 py-0.5 text-[10px] overline mr-1">Todo Inc.</span>}
                                                {p.active === false ? <span className="bg-muted text-muted-foreground px-2 py-0.5 text-[10px] overline">Pausado</span> : <span className="text-eucalyptus text-[10px] overline">Activo</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <Button size="sm" variant="ghost" onClick={() => setEditing({ ...p })} data-testid={`edit-provider-${p.id}`} className="rounded-none text-xs">Editar</Button>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-4">{editing?._isNew ? "Nuevo proveedor" : "Editor"}</h3>
                {!editing ? <div className="overline text-muted-foreground">Selecciona un proveedor o presiona &quot;Nuevo proveedor&quot;.</div> : (
                    <div className="space-y-3" data-testid="provider-editor">
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Categoría">
                                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full border border-border rounded-none px-2 py-1.5 bg-white text-sm" data-testid="edit-category">
                                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                </select>
                            </Field>
                            <Field label="Nivel">
                                <select value={editing.tier} onChange={(e) => setEditing({ ...editing, tier: e.target.value })} className="w-full border border-border rounded-none px-2 py-1.5 bg-white text-sm" data-testid="edit-tier">
                                    <option value="basico">Básico</option>
                                    <option value="normal">Estándar</option>
                                    <option value="premium">Premium</option>
                                </select>
                            </Field>
                        </div>
                        <Field label="Marca / Empresa">
                            <Input value={editing.brand || ""} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} placeholder="Ej: Barlovento" className="rounded-none" data-testid="edit-brand" />
                        </Field>
                        <Field label="Nombre del paquete">
                            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-none" data-testid="edit-name" />
                        </Field>
                        <Field label="Descripción">
                            <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-none" data-testid="edit-description" />
                        </Field>
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Modo precio">
                                <select value={editing.price_mode} onChange={(e) => setEditing({ ...editing, price_mode: e.target.value })} className="w-full border border-border rounded-none px-2 py-1.5 bg-white text-sm" data-testid="edit-price-mode">
                                    <option value="flat">Fijo</option>
                                    <option value="per_guest">Por invitado</option>
                                </select>
                            </Field>
                            <Field label="Precio">
                                <Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="rounded-none tabular" data-testid="edit-price" />
                            </Field>
                        </div>
                        <Field label="Instagram / Portafolio URL">
                            <Input value={editing.instagram_url || ""} onChange={(e) => setEditing({ ...editing, instagram_url: e.target.value })} className="rounded-none" data-testid="edit-instagram" />
                        </Field>
                        <Field label="Logo URL (imagen)">
                            <Input value={editing.logo_url || ""} onChange={(e) => setEditing({ ...editing, logo_url: e.target.value })} placeholder="https://…/logo.png" className="rounded-none" data-testid="edit-logo" />
                        </Field>

                        <div className="flex items-center gap-3 border border-border p-3">
                            <Checkbox checked={editing.active !== false} onCheckedChange={(v) => setEditing({ ...editing, active: !!v })} className="rounded-none" data-testid="edit-active" />
                            <span className="text-xs"><strong>Activo</strong> — desmarca para pausar sin eliminar</span>
                        </div>

                        {editing.category === "wedding_planner" && (
                            <div className="flex items-start gap-3 bg-accent/10 border-l-2 border-accent p-3" data-testid="wp-all-inclusive-toggle">
                                <Checkbox checked={!!editing.all_inclusive} onCheckedChange={(v) => setEditing({ ...editing, all_inclusive: !!v })} className="rounded-none mt-0.5" data-testid="edit-all-inclusive" />
                                <div className="text-xs">
                                    <strong className="text-accent">Todo Incluido (Bloqueo Mix & Match)</strong>
                                    <p className="text-muted-foreground mt-0.5">Al activar, este paquete cerrará automáticamente Catering, Decoración, Música, Fotografía, Video y Estructura al cliente.</p>
                                </div>
                            </div>
                        )}

                        <Field label="Servicios incluidos (uno por línea)">
                            <textarea
                                value={(editing.included || []).join("\n")}
                                onChange={(e) => setEditing({ ...editing, included: e.target.value.split("\n").filter(Boolean) })}
                                className="w-full border border-border rounded-none px-2 py-1.5 bg-white text-xs h-24 font-mono"
                                data-testid="edit-included"
                            />
                        </Field>

                        <div className="flex gap-2 pt-2">
                            <Button onClick={save} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/85" data-testid="btn-save-provider">
                                {editing._isNew ? "Crear" : "Guardar"}
                            </Button>
                            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none">Cancelar</Button>
                            {!editing._isNew && (
                                <Button variant="ghost" onClick={remove} className="rounded-none text-destructive ml-auto" data-testid="btn-delete-provider">Eliminar</Button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------- SERVICIOS ADICIONALES ----------------
function ExtrasTab() {
    const [items, setItems] = useState([]);
    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/extra-services"); setItems(data); }
        catch (err) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const update = (idx, patch) => setItems(items.map((x, i) => i === idx ? { ...x, ...patch } : x));

    const save = async (s) => {
        try {
            await api.put(`/admin/extra-services/${s.id}`, {
                id: s.id, name: s.name, description: s.description, type: s.type,
                options: (s.options || []).map((o) => ({ label: o.label, price: Number(o.price) || 0 })),
                unit_price: Number(s.unit_price) || 0, unit_label: s.unit_label || "",
                price: Number(s.price) || 0, per_guest_price: Number(s.per_guest_price) || 0,
                provider_ref: s.provider_ref || "",
                active: s.active !== false, order: Number(s.order) || 0,
            });
            toast.success("Servicio guardado");
        } catch (_e) { toast.error("Error al guardar"); }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((s, idx) => (
                <div key={s.id} className="bg-white border border-border p-5" data-testid={`extra-editor-${s.id}`}>
                    <div className="flex items-center justify-between">
                        <h4 className="font-display text-xl">{s.name}</h4>
                        <Checkbox checked={s.active !== false} onCheckedChange={(v) => update(idx, { active: !!v })} className="rounded-none" data-testid={`extra-active-${s.id}`} />
                    </div>
                    <div className="overline text-muted-foreground mt-1 text-[10px]">{s.type}</div>
                    <div className="mt-4 space-y-3">
                        <Field label="Descripción">
                            <Input value={s.description} onChange={(e) => update(idx, { description: e.target.value })} className="rounded-none text-xs" />
                        </Field>
                        {s.type === "selector" && (s.options || []).map((o, oi) => (
                            <div key={oi} className="grid grid-cols-2 gap-2">
                                <Input value={o.label} onChange={(e) => update(idx, { options: s.options.map((x, j) => j === oi ? { ...x, label: e.target.value } : x) })} className="rounded-none text-xs" />
                                <Input type="number" value={o.price} onChange={(e) => update(idx, { options: s.options.map((x, j) => j === oi ? { ...x, price: e.target.value } : x) })} className="rounded-none tabular text-xs" />
                            </div>
                        ))}
                        {s.type === "counter" && (
                            <div className="grid grid-cols-2 gap-2">
                                <Field label={`$ por ${s.unit_label || "unidad"}`}>
                                    <Input type="number" value={s.unit_price} onChange={(e) => update(idx, { unit_price: e.target.value })} className="rounded-none tabular text-xs" />
                                </Field>
                                <Field label="Unidad">
                                    <Input value={s.unit_label} onChange={(e) => update(idx, { unit_label: e.target.value })} className="rounded-none text-xs" />
                                </Field>
                            </div>
                        )}
                        {s.type === "switch" && (
                            <Field label="Precio fijo"><Input type="number" value={s.price} onChange={(e) => update(idx, { price: e.target.value })} className="rounded-none tabular text-xs" /></Field>
                        )}
                        {s.type === "per_guest" && (
                            <Field label="$ por invitado"><Input type="number" step="0.5" value={s.per_guest_price} onChange={(e) => update(idx, { per_guest_price: e.target.value })} className="rounded-none tabular text-xs" /></Field>
                        )}
                        <Field label="Proveedor de referencia (opcional)">
                            <Input value={s.provider_ref || ""} onChange={(e) => update(idx, { provider_ref: e.target.value })} className="rounded-none text-xs" data-testid={`extra-provref-${s.id}`} />
                        </Field>
                        <Button size="sm" onClick={() => save(items[idx])} className="rounded-none bg-foreground text-background" data-testid={`btn-save-extra-${s.id}`}>Guardar</Button>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ---------------- FAQ (administrable) ----------------
function FaqTab() {
    const [items, setItems] = useState([]);
    const [draft, setDraft] = useState({ q: "", a: "" });
    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/faqs"); setItems(data); }
        catch (err) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!draft.q || !draft.a) { toast.error("Pregunta y respuesta requeridas"); return; }
        await api.post("/admin/faqs", { q: draft.q, a: draft.a, order: items.length + 1, active: true });
        setDraft({ q: "", a: "" });
        toast.success("FAQ agregada");
        load();
    };
    const save = async (f) => {
        await api.put(`/admin/faqs/${f.id}`, { q: f.q, a: f.a, order: Number(f.order) || 0, active: f.active !== false });
        toast.success("FAQ guardada");
    };
    const del = async (id) => { await api.delete(`/admin/faqs/${id}`); load(); };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-3">
                <p className="text-xs text-muted-foreground">Estas preguntas se muestran en el resumen del cotizador. Aliméntalas con las dudas reales de tus clientes.</p>
                {items.map((f, idx) => (
                    <div key={f.id} className="bg-white border border-border p-4 space-y-2" data-testid={`faq-editor-${f.id}`}>
                        <div className="flex items-center gap-2">
                            <Checkbox checked={f.active !== false} onCheckedChange={(v) => setItems(items.map((x, i) => i === idx ? { ...x, active: !!v } : x))} className="rounded-none" />
                            <Input value={f.q} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, q: e.target.value } : x))} className="rounded-none text-sm font-medium" />
                        </div>
                        <textarea
                            value={f.a}
                            onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, a: e.target.value } : x))}
                            rows={2}
                            className="w-full border border-border rounded-none p-2 text-xs focus:outline-none focus:border-foreground"
                        />
                        <div className="flex gap-2">
                            <Button size="sm" onClick={() => save(items[idx])} className="rounded-none bg-foreground text-background text-xs" data-testid={`btn-save-faq-${f.id}`}>Guardar</Button>
                            <Button size="sm" variant="ghost" onClick={() => del(f.id)} className="rounded-none text-destructive text-xs" data-testid={`btn-del-faq-${f.id}`}>Eliminar</Button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="bg-white border border-border p-6 space-y-3 h-fit">
                <h3 className="font-display text-2xl">Nueva pregunta</h3>
                <Field label="Pregunta"><Input value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} className="rounded-none" data-testid="input-faq-q" /></Field>
                <Field label="Respuesta">
                    <textarea value={draft.a} onChange={(e) => setDraft({ ...draft, a: e.target.value })} rows={4} className="w-full border border-border rounded-none p-2 text-sm focus:outline-none focus:border-foreground" data-testid="input-faq-a" />
                </Field>
                <Button onClick={add} className="rounded-none bg-foreground text-background" data-testid="btn-add-faq">Agregar FAQ</Button>
            </div>
        </div>
    );
}

// ---------------- BENEFICIOS ----------------
function BenefitsTab() {
    const [items, setItems] = useState([]);
    const [draft, setDraft] = useState({ partner_name: "", category: "", description: "", discount_text: "" });
    const [editingId, setEditingId] = useState(null);
    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/benefits"); setItems(data); }
        catch (err) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);
    const resetDraft = () => { setDraft({ partner_name: "", category: "", description: "", discount_text: "" }); setEditingId(null); };
    const save = async () => {
        if (editingId) {
            await api.put(`/admin/benefits/${editingId}`, draft);
            toast.success("Beneficio actualizado");
        } else {
            await api.post("/admin/benefits", draft);
            toast.success("Beneficio agregado");
        }
        resetDraft();
        load();
    };
    const startEdit = (b) => {
        setEditingId(b.id);
        setDraft({ partner_name: b.partner_name || "", category: b.category || "", description: b.description || "", discount_text: b.discount_text || "" });
    };
    const del = async (id) => { await api.delete(`/admin/benefits/${id}`); if (id === editingId) resetDraft(); load(); };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 grid md:grid-cols-2 gap-3">
                {items.map((b) => (
                    <div key={b.id} className={`bg-white border p-5 ${editingId === b.id ? "border-primary" : "border-border"}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="overline text-eucalyptus">{b.category}</div>
                                <h4 className="font-display text-xl mt-1">{b.partner_name}</h4>
                            </div>
                            <div className="flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => startEdit(b)} className="rounded-none text-xs" data-testid={`edit-benefit-${b.id}`}>Editar</Button>
                                <Button size="sm" variant="ghost" onClick={() => del(b.id)} className="rounded-none text-xs text-destructive" data-testid={`del-benefit-${b.id}`}>Eliminar</Button>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">{b.description}</p>
                        <div className="overline text-accent mt-3">{b.discount_text}</div>
                    </div>
                ))}
            </div>
            <div className="bg-white border border-border p-6 space-y-3">
                <h3 className="font-display text-2xl">{editingId ? "Editar beneficio" : "Nuevo beneficio"}</h3>
                <Field label="Partner"><Input value={draft.partner_name} onChange={(e) => setDraft({ ...draft, partner_name: e.target.value })} className="rounded-none" data-testid="input-benefit-partner" /></Field>
                <Field label="Categoría"><Input value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="rounded-none" data-testid="input-benefit-category" /></Field>
                <Field label="Descripción"><Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="rounded-none" data-testid="input-benefit-desc" /></Field>
                <Field label="Descuento"><Input value={draft.discount_text} onChange={(e) => setDraft({ ...draft, discount_text: e.target.value })} className="rounded-none" data-testid="input-benefit-discount" /></Field>
                <div className="flex gap-2">
                    <Button onClick={save} className="rounded-none bg-foreground text-background" data-testid="btn-add-benefit">{editingId ? "Guardar cambios" : "Agregar"}</Button>
                    {editingId && <Button variant="outline" onClick={resetDraft} className="rounded-none" data-testid="btn-cancel-benefit-edit">Cancelar</Button>}
                </div>
            </div>
        </div>
    );
}

// ---------------- ANIVERSARIOS ----------------
function AnniversaryTab() {
    const [data, setData] = useState({ scheduled: [], logs: [], pending_now: [] });
    const load = useCallback(async () => {
        try { const r = await api.get("/admin/anniversaries"); setData(r.data); }
        catch (err) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);
    const markSent = async (id, kind) => {
        await api.post(`/admin/anniversaries/${id}/mark-sent?kind=${kind}`);
        toast.success("Marcado como enviado");
        load();
    };
    const kindBadge = (kind) => kind === "11m"
        ? <span className="bg-secondary text-foreground px-2 py-0.5 text-[10px] overline">11 meses · promo</span>
        : <span className="bg-accent text-accent-foreground px-2 py-0.5 text-[10px] overline">1 año</span>;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-3">Pendientes hoy</h3>
                {data.pending_now.length === 0 ? <div className="overline text-muted-foreground">Sin pendientes</div> : data.pending_now.map((l) => (
                    <div key={`${l.id}-${l.kind}`} className="border-b border-border py-3">
                        <div className="flex items-center gap-2">
                            <span className="font-display text-lg">{l.bride_name && l.groom_name ? `${l.bride_name} & ${l.groom_name}` : l.contact_name}</span>
                            {kindBadge(l.kind)}
                        </div>
                        <div className="text-xs text-muted-foreground">{l.email || l.phone}</div>
                        <Button size="sm" onClick={() => markSent(l.id, l.kind)} className="mt-2 rounded-none bg-accent text-white" data-testid={`send-anniv-${l.id}-${l.kind}`}>Marcar como enviado</Button>
                    </div>
                ))}
            </div>
            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-3">Programados</h3>
                <ul className="text-xs space-y-2">
                    {data.scheduled.map((l) => (
                        <li key={l.id} className="border-b border-border pb-2">
                            <div className="flex justify-between">
                                <span>{l.bride_name && l.groom_name ? `${l.bride_name} & ${l.groom_name}` : l.contact_name}</span>
                                <span className="tabular text-muted-foreground">{(l.anniversary_scheduled_for || "").slice(0, 10)}</span>
                            </div>
                            {l.anniversary_11m_scheduled_for && (
                                <div className="text-muted-foreground mt-0.5">Alerta 11 meses: <span className="tabular">{l.anniversary_11m_scheduled_for.slice(0, 10)}</span></div>
                            )}
                        </li>
                    ))}
                    {data.scheduled.length === 0 && <li className="overline text-muted-foreground">Sin programados</li>}
                </ul>
            </div>
            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-3">Historial enviado</h3>
                <ul className="text-xs space-y-2">
                    {data.logs.map((l) => (
                        <li key={`${l.lead_id}-${l.kind || "1y"}-${l.sent_at}`} className="border-b border-border pb-2">
                            <div className="flex items-center gap-2">{l.couple} {kindBadge(l.kind || "1y")}</div>
                            <div className="text-muted-foreground tabular">{(l.sent_at || "").slice(0, 16).replace("T", " ")}</div>
                        </li>
                    ))}
                    {data.logs.length === 0 && <li className="overline text-muted-foreground">Sin historial</li>}
                </ul>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline text-muted-foreground">{label}</Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function NotifyTab() {
    const [data, setData] = useState({ admin_emails: [], resend_active: false });
    const [draftEmails, setDraftEmails] = useState("");
    const [site, setSite] = useState({ whatsapp_number: "", calendly_url: "" });
    const [emailCfg, setEmailCfg] = useState({ resend_active: false, resend_key_masked: "", resend_from_email: "" });
    const [keyDraft, setKeyDraft] = useState("");
    const [fromDraft, setFromDraft] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/admin/notify-settings");
            setData(d);
            setDraftEmails((d.admin_emails || []).join(", "));
        } catch (err) { /* noop */ }
        try {
            const { data: s } = await api.get("/admin/site-settings");
            setSite({ whatsapp_number: s.whatsapp_number || "", calendly_url: s.calendly_url || "" });
        } catch (err) { /* noop */ }
        try {
            const { data: ec } = await api.get("/admin/email-settings");
            setEmailCfg(ec);
            setFromDraft(ec.resend_from_email || "");
        } catch (err) { /* noop */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const saveEmailCfg = async () => {
        if (!keyDraft.trim()) { toast.error("Pega tu API key de Resend (re_...)"); return; }
        try {
            const { data: ec } = await api.put("/admin/email-settings", {
                resend_api_key: keyDraft.trim(),
                resend_from_email: fromDraft.trim(),
            });
            setEmailCfg(ec);
            setKeyDraft("");
            setData((prev) => ({ ...prev, resend_active: ec.resend_active }));
            toast.success("Resend activado — los emails ahora se envían de verdad ✉️");
        } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar la configuración"); }
    };

    const disableEmailCfg = async () => {
        if (!window.confirm("¿Desactivar el envío real de emails y volver al modo log-only?")) return;
        try {
            const { data: ec } = await api.delete("/admin/email-settings");
            setEmailCfg(ec);
            setData((prev) => ({ ...prev, resend_active: false }));
            toast.success("Envío real desactivado (modo log-only)");
        } catch (e) { toast.error("Error al desactivar"); }
    };

    const saveSite = async () => {
        try {
            await api.put("/admin/site-settings", site);
            toast.success("Ajustes del sitio guardados (WhatsApp / Calendly)");
        } catch (e) { toast.error("Error al guardar ajustes"); }
    };

    const save = async () => {
        const list = draftEmails.split(",").map((e) => e.trim()).filter(Boolean);
        try {
            const { data: d } = await api.put("/admin/notify-settings", { admin_emails: list });
            setData(d);
            toast.success("Emails de notificación guardados");
        } catch (e) { toast.error("Error al guardar"); }
    };

    const sendTest = async () => {
        try {
            const { data: r } = await api.post("/admin/notify-settings/test");
            if (r.sent) toast.success(`Email de prueba enviado a ${r.recipients.length} destinatario(s)`);
            else toast.info("Resend desactivado · log-only · revisa logs del backend");
        } catch (e) { toast.error("Error al enviar prueba"); }
    };

    if (loading) return <div className="overline text-muted-foreground">Cargando…</div>;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-2">Ajustes del sitio</h3>
                <p className="text-xs text-muted-foreground mb-4">Número de WhatsApp (con código de país, sin +) y enlace de Calendly usados en todos los botones del cotizador.</p>
                <div className="space-y-3">
                    <Field label="WhatsApp (ej: 593991234567)">
                        <Input value={site.whatsapp_number} onChange={(e) => setSite({ ...site, whatsapp_number: e.target.value })} className="rounded-none tabular" data-testid="input-whatsapp-number" />
                    </Field>
                    <Field label="Enlace de Calendly">
                        <Input value={site.calendly_url} onChange={(e) => setSite({ ...site, calendly_url: e.target.value })} className="rounded-none" data-testid="input-calendly-url" />
                    </Field>
                    <Button onClick={saveSite} className="rounded-none bg-foreground text-background" data-testid="btn-save-site">Guardar ajustes</Button>
                </div>
            </div>

            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-2">Destinatarios admin</h3>
                <p className="text-xs text-muted-foreground mb-4">
                    Cada lead nuevo envía una notificación con el resumen a estos emails. Separados por coma.
                </p>
                <textarea
                    value={draftEmails}
                    onChange={(e) => setDraftEmails(e.target.value)}
                    rows={3}
                    className="w-full border border-border rounded-none p-3 text-sm focus:outline-none focus:border-foreground"
                    data-testid="input-admin-emails"
                />
                <div className="flex gap-2 mt-3">
                    <Button onClick={save} className="rounded-none bg-foreground text-background" data-testid="btn-save-emails">Guardar</Button>
                    <Button variant="outline" onClick={sendTest} className="rounded-none" data-testid="btn-test-email">Enviar prueba</Button>
                </div>
            </div>

            <div className="bg-white border border-border p-6">
                <h3 className="font-display text-2xl mb-2">Estado de Resend</h3>
                <div className={`overline ${emailCfg.resend_active ? "text-eucalyptus" : "text-accent"}`} data-testid="resend-status">
                    {emailCfg.resend_active ? "● Resend ACTIVO — envío real" : "○ Resend INACTIVO — modo log-only"}
                </div>

                <div className="mt-4 border-t border-border pt-4 space-y-3" data-testid="resend-config">
                    <div className="overline text-muted-foreground text-[10px]">Activar envío real de emails</div>
                    {emailCfg.resend_active ? (
                        <div className="text-xs space-y-2">
                            <div>API key: <span className="tabular font-mono" data-testid="resend-key-masked">{emailCfg.resend_key_masked}</span></div>
                            <div>Remitente: <span className="font-mono">{emailCfg.resend_from_email || "—"}</span></div>
                            <div className="flex gap-2 pt-1">
                                <Button size="sm" variant="outline" onClick={disableEmailCfg} className="rounded-none text-destructive" data-testid="btn-disable-resend">Desactivar</Button>
                            </div>
                            <p className="text-muted-foreground">Para cambiar la key, desactiva y vuelve a guardar una nueva.</p>
                        </div>
                    ) : (
                        <>
                            <p className="text-xs text-muted-foreground">
                                1. Crea tu cuenta en <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a> y verifica tu dominio (Domains → Add Domain).<br/>
                                2. Genera una API key (API Keys → Create) y pégala aquí junto con el remitente autorizado.
                            </p>
                            <Field label="API key de Resend (re_...)">
                                <Input
                                    type="password"
                                    value={keyDraft}
                                    onChange={(e) => setKeyDraft(e.target.value)}
                                    placeholder="re_XXXXXXXXXXXX"
                                    className="rounded-none font-mono text-xs"
                                    data-testid="input-resend-key"
                                />
                            </Field>
                            <Field label="Remitente autorizado (dominio verificado)">
                                <Input
                                    value={fromDraft}
                                    onChange={(e) => setFromDraft(e.target.value)}
                                    placeholder="Hacienda Nápoles <bodas@tudominio.com>"
                                    className="rounded-none text-xs"
                                    data-testid="input-resend-from"
                                />
                            </Field>
                            <Button onClick={saveEmailCfg} className="rounded-none bg-primary text-primary-foreground" data-testid="btn-save-resend">Activar envío real</Button>
                            <p className="text-[10px] text-muted-foreground">
                                Al activarlo se envían de verdad: notificación de leads al equipo, contratos para firma, recuperación de contraseña y alertas de aniversario. Usa &quot;Enviar prueba&quot; para verificar la entrega.
                            </p>
                        </>
                    )}
                </div>

                <div className="mt-6 border-t border-border pt-4 text-xs">
                    <div className="overline text-muted-foreground">Scheduler de aniversarios</div>
                    <div className="mt-1 text-eucalyptus">● ACTIVO — corre diario a las 10:00 UTC</div>
                    <p className="text-muted-foreground mt-2">
                        Envía alertas automáticas a los <strong>11 meses</strong> (promoción de aniversario) y al <strong>1 año</strong> (felicitación) del evento. Con Resend inactivo, quedan registradas en la pestaña Aniversarios.
                    </p>
                </div>
                <div className="mt-4 border-t border-border pt-4 text-xs">
                    <div className="overline text-muted-foreground">Recuperación de contraseña</div>
                    <p className="text-muted-foreground mt-2">
                        Flujo activo en /reset-password con token de expiración de 1 hora. Los enlaces se envían por email (log-only hasta activar Resend).
                    </p>
                </div>
            </div>
        </div>
    );
}

function CustomersTab() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/customers"); setCustomers(data); }
        catch (err) { /* noop */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);
    return (
        <div className="bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-2xl">Clientes registrados</h3>
                <Button variant="outline" size="sm" onClick={load} className="rounded-none" data-testid="btn-refresh-customers">
                    <RefreshCw className="h-3 w-3 mr-1" /> Recargar
                </Button>
            </div>
            {loading ? <div className="overline text-muted-foreground">Cargando…</div> : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="customers-table">
                        <thead className="bg-secondary">
                            <tr>{["Registrado", "Nombre", "Email", "# Cotizaciones"].map((h) => <th key={h} className="text-left px-3 py-2 overline">{h}</th>)}</tr>
                        </thead>
                        <tbody>
                            {customers.map((c) => (
                                <tr key={c.id} className="border-t border-border hover:bg-secondary/50">
                                    <td className="px-3 py-2 tabular">{(c.created_at || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2 font-display text-base">{c.name || "—"}</td>
                                    <td className="px-3 py-2">{c.email}</td>
                                    <td className="px-3 py-2 tabular text-right">{c.lead_count}</td>
                                </tr>
                            ))}
                            {customers.length === 0 && <tr><td colSpan={4} className="text-center text-muted-foreground py-10 overline">Sin clientes aún</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
